import { useState } from 'react';
import { ChessAppLayout } from '@/components/ChessAppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, 
  Shield, 
  AlertTriangle, 
  Mail, 
  User, 
  Clock,
  FileText,
  Plus
} from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';
import { useSEO } from '@/hooks/useSEO';
import { useAdminInvestigation, type PlayerReport } from '@/hooks/useAdminInvestigation';
import { CheatingInvestigationPanel } from '@/components/admin/CheatingInvestigationPanel';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function AdminCheatingInvestigation() {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const {
    reports,
    flaggedGames,
    loading,
    investigatedPlayer,
    searchPlayer,
    investigatePlayer,
    updateReportStatus,
    updateFlagStatus,
    addReport,
    issueSanction,
    revokeSanction,
    clearInvestigation
  } = useAdminInvestigation();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [addReportOpen, setAddReportOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'reports' | 'flagged'>('flagged');
  const [newReport, setNewReport] = useState({
    reporter_email: '',
    reported_player_id: '',
    reason: '',
    description: ''
  });

  useSEO({
    title: 'Cheating Investigation - Admin',
    description: 'Admin tool for investigating cheating reports',
    robots: 'noindex, nofollow'
  });

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    const results = await searchPlayer(searchQuery);
    setSearchResults(results);
    setSearching(false);
  };

  const handleAddReport = async () => {
    if (!newReport.reporter_email || !newReport.reported_player_id || !newReport.reason) return;
    await addReport(newReport);
    setAddReportOpen(false);
    setNewReport({ reporter_email: '', reported_player_id: '', reason: '', description: '' });
  };

  const pendingReports = reports.filter(r => r.status === 'pending');
  const investigatingReports = reports.filter(r => r.status === 'investigating');
  const pendingFlags = flaggedGames.filter(f => f.status === 'pending');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-orange-500/10 text-orange-500 border-orange-500/30';
      case 'investigating': return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
      case 'resolved': 
      case 'cleared': return 'bg-green-500/10 text-green-500 border-green-500/30';
      case 'dismissed':
      case 'confirmed': return 'bg-destructive/10 text-destructive border-destructive/30';
      default: return '';
    }
  };

  const getVerdictColor = (verdict: string) => {
    if (verdict === 'likely_cheating') return 'bg-destructive/10 text-destructive border-destructive/30';
    return 'bg-orange-500/10 text-orange-500 border-orange-500/30';
  };

  if (adminLoading) {
    return (
      <ChessAppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
        </div>
      </ChessAppLayout>
    );
  }

  if (!isAdmin) {
    return (
      <ChessAppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <Shield className="h-16 w-16 text-destructive mx-auto" />
            <h1 className="text-2xl font-bold text-destructive">Access Denied</h1>
          </div>
        </div>
      </ChessAppLayout>
    );
  }

  return (
    <ChessAppLayout>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Cheating Investigation</h1>
            <p className="text-sm text-muted-foreground">
              Review reports and investigate suspicious players
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Search */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Search Player
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Search by username..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button onClick={handleSearch} disabled={searching}>
                    {searching ? 'Searching...' : 'Search'}
                  </Button>
                </div>
                
                {searchResults.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {searchResults.map(player => (
                      <div 
                        key={player.id}
                        className="p-3 bg-muted/50 rounded-lg flex items-center justify-between cursor-pointer hover:bg-muted transition-colors"
                        onClick={() => investigatePlayer(player.id)}
                      >
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{player.display_name || player.username}</span>
                          <span className="text-xs text-muted-foreground">@{player.username}</span>
                        </div>
                        <Badge variant="outline">{player.elo_rating} ELO</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Main Tabs - Auto-Flagged vs Reports */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'reports' | 'flagged')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="flagged" className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Auto-Flagged
                  {pendingFlags.length > 0 && (
                    <Badge variant="destructive" className="ml-1 px-1.5 py-0 text-xs">
                      {pendingFlags.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="reports" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Reports
                  {pendingReports.length > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                      {pendingReports.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Auto-Flagged Games Tab */}
              <TabsContent value="flagged" className="mt-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Auto-Detected Suspicious Games</CardTitle>
                    <CardDescription>
                      Games automatically flagged by the detection system
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {flaggedGames.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No auto-flagged games yet
                        </p>
                      ) : (
                        flaggedGames.map(flag => (
                          <div 
                            key={flag.id}
                            className="p-3 bg-muted/30 rounded-lg border border-border/50"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <p className="font-medium text-sm">
                                  {flag.player?.display_name || flag.player?.username || 'Unknown'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Score: {Math.round(flag.suspicion_score)}%
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={getVerdictColor(flag.verdict)}>
                                  {flag.verdict === 'likely_cheating' ? 'Likely Cheating' : 'Suspicious'}
                                </Badge>
                                <Badge className={getStatusColor(flag.status)}>
                                  {flag.status}
                                </Badge>
                              </div>
                            </div>
                            
                            {flag.flags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {flag.flags.map((f, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {f}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {format(new Date(flag.created_at), 'MMM d, h:mm a')}
                              </span>
                              <div className="flex gap-1">
                                {flag.status === 'pending' && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => {
                                        investigatePlayer(flag.player_id);
                                        updateFlagStatus(flag.id, 'investigating');
                                      }}
                                    >
                                      Investigate
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => updateFlagStatus(flag.id, 'cleared', 'False positive')}
                                    >
                                      Clear
                                    </Button>
                                  </>
                                )}
                                {flag.status === 'investigating' && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="destructive"
                                      onClick={() => updateFlagStatus(flag.id, 'confirmed')}
                                    >
                                      Confirm
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => updateFlagStatus(flag.id, 'cleared')}
                                    >
                                      Clear
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Email Reports Tab */}
              <TabsContent value="reports" className="mt-4">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">Email Reports Queue</CardTitle>
                        <CardDescription>
                          {pendingReports.length} pending, {investigatingReports.length} investigating
                        </CardDescription>
                      </div>
                      <Button size="sm" onClick={() => setAddReportOpen(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Report
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="pending">
                      <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="pending">Pending ({pendingReports.length})</TabsTrigger>
                        <TabsTrigger value="investigating">Investigating</TabsTrigger>
                        <TabsTrigger value="resolved">Resolved</TabsTrigger>
                        <TabsTrigger value="dismissed">Dismissed</TabsTrigger>
                      </TabsList>

                      {['pending', 'investigating', 'resolved', 'dismissed'].map(status => (
                        <TabsContent key={status} value={status} className="mt-3">
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {reports.filter(r => r.status === status).length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                No {status} reports
                              </p>
                            ) : (
                              reports.filter(r => r.status === status).map(report => (
                                <ReportCard 
                                  key={report.id}
                                  report={report}
                                  onInvestigate={() => {
                                    investigatePlayer(report.reported_player_id);
                                    updateReportStatus(report.id, 'investigating');
                                  }}
                                  onUpdateStatus={updateReportStatus}
                                />
                              ))
                            )}
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Investigation Panel */}
          <div className="lg:col-span-1">
            {investigatedPlayer ? (
              <CheatingInvestigationPanel
                player={investigatedPlayer}
                onClose={clearInvestigation}
                onIssueSanction={issueSanction}
                onRevokeSanction={revokeSanction}
              />
            ) : (
              <Card className="h-full min-h-[300px] flex items-center justify-center">
                <CardContent className="text-center">
                  <User className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    Search for a player or select a report to investigate
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Add Report Dialog */}
      <Dialog open={addReportOpen} onOpenChange={setAddReportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Report from Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reporter Email</Label>
              <Input
                value={newReport.reporter_email}
                onChange={(e) => setNewReport({...newReport, reporter_email: e.target.value})}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Reported Player ID</Label>
              <Input
                value={newReport.reported_player_id}
                onChange={(e) => setNewReport({...newReport, reported_player_id: e.target.value})}
                placeholder="UUID of the reported player"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select
                value={newReport.reason}
                onValueChange={(v) => setNewReport({...newReport, reason: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="suspected_cheating">Suspected Cheating</SelectItem>
                  <SelectItem value="engine_assistance">Engine Assistance</SelectItem>
                  <SelectItem value="sandbagging">Sandbagging/Rating Manipulation</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newReport.description}
                onChange={(e) => setNewReport({...newReport, description: e.target.value})}
                placeholder="Details from the report..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddReportOpen(false)}>Cancel</Button>
            <Button onClick={handleAddReport}>Add Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ChessAppLayout>
  );
}

function ReportCard({ 
  report, 
  onInvestigate,
  onUpdateStatus 
}: { 
  report: PlayerReport;
  onInvestigate: () => void;
  onUpdateStatus: (id: string, status: any, notes?: string) => void;
}) {
  const statusColors: Record<string, string> = {
    pending: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
    investigating: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
    resolved: 'bg-green-500/10 text-green-500 border-green-500/30',
    dismissed: 'bg-muted text-muted-foreground'
  };

  return (
    <div className="p-3 bg-muted/30 rounded-lg">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="font-medium text-sm">
            {report.reported_player?.display_name || report.reported_player?.username || 'Unknown'}
          </p>
          <p className="text-xs text-muted-foreground">{report.reporter_email}</p>
        </div>
        <Badge className={statusColors[report.status]}>
          {report.status}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground mb-2">{report.reason}</p>
      {report.description && (
        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{report.description}</p>
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {format(new Date(report.created_at), 'MMM d, h:mm a')}
        </span>
        {report.status === 'pending' && (
          <Button size="sm" variant="outline" onClick={onInvestigate}>
            Investigate
          </Button>
        )}
      </div>
    </div>
  );
}
